import React, { useState, useEffect } from 'react';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import { portalEmailPreferencesAPI } from '../../services/portalAPI';
import PortalFooter from '../../components/Layout/PortalFooter';
import '../../styles/portal-brand.css';

interface EmailPreferences {
  order_confirmed: boolean;
  order_in_production: boolean;
  order_packaged: boolean;
  order_shipped: boolean;
  order_ready_for_pickup: boolean;
  order_delivered: boolean;
  invoice_sent: boolean;
  payment_received: boolean;
  payment_reminder: boolean;
  marketing_emails: boolean;
  daily_digest: boolean;
  laura_voice_enabled: boolean;
}

const EmailPreferences: React.FC = () => {
  usePortalAuth();
  const [preferences, setPreferences] = useState<EmailPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const response = await portalEmailPreferencesAPI.get();
      setPreferences(response.data);
      // Sync laura voice setting to localStorage for cross-page access
      try { localStorage.setItem('laura_voice_enabled', response.data.laura_voice_enabled ? '1' : '0'); } catch { /* ignore */ }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load email preferences');
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    if (!preferences) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const response = await portalEmailPreferencesAPI.update(preferences);
      setPreferences(response.data);
      setSuccess('Email preferences saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save email preferences');
    } finally {
      setSaving(false);
    }
  };

  const togglePreference = (key: keyof EmailPreferences) => {
    if (!preferences) return;
    const newValue = !preferences[key];
    const updated = { ...preferences, [key]: newValue };
    setPreferences(updated);
    // Persist laura_voice_enabled to localStorage for instant cross-page access
    if (key === 'laura_voice_enabled') {
      try { localStorage.setItem('laura_voice_enabled', newValue ? '1' : '0'); } catch { /* ignore */ }
    }
    // Auto-save every toggle immediately so changes persist on navigation
    portalEmailPreferencesAPI.update(updated).catch(() => {});
  };

  if (loading) {
    return (
      <div className="portal-page">
        <div className="portal-container">
          <div className="portal-loading">Loading email preferences...</div>
        </div>
        <PortalFooter />
      </div>
    );
  }

  return (
    <div className="portal-page">
      <div className="portal-container">
        <div className="portal-header">
          <h1>Email Preferences</h1>
          <p className="portal-subtitle">Manage which email notifications you receive from Red Bridge Farm Feed Co.</p>
        </div>

        {error && <div className="portal-error">{error}</div>}
        {success && <div className="portal-success">{success}</div>}

        {preferences && (
          <div className="preferences-container">
            {/* Order Updates Section */}
            <div className="preference-section">
              <h2 className="section-title">Order Updates</h2>
              <p className="section-description">Stay informed about your order status</p>

              <div className="preference-group">
                <PreferenceToggle
                  label="Order Confirmed"
                  description="Receive confirmation when your order is placed"
                  checked={preferences.order_confirmed}
                  onChange={() => togglePreference('order_confirmed')}
                />
                <PreferenceToggle
                  label="Order In Production"
                  description="Get notified when your feed starts being milled"
                  checked={preferences.order_in_production}
                  onChange={() => togglePreference('order_in_production')}
                />
                <PreferenceToggle
                  label="Order Packaged"
                  description="Know when your order is packaged and ready"
                  checked={preferences.order_packaged}
                  onChange={() => togglePreference('order_packaged')}
                />
                <PreferenceToggle
                  label="Order Shipped"
                  description="Track when your order is on its way"
                  checked={preferences.order_shipped}
                  onChange={() => togglePreference('order_shipped')}
                />
                <PreferenceToggle
                  label="Ready for Pickup"
                  description="Get notified when your order is ready for pickup"
                  checked={preferences.order_ready_for_pickup}
                  onChange={() => togglePreference('order_ready_for_pickup')}
                />
                <PreferenceToggle
                  label="Order Delivered"
                  description="Confirmation when your order has been delivered"
                  checked={preferences.order_delivered}
                  onChange={() => togglePreference('order_delivered')}
                />
              </div>
            </div>

            {/* Billing & Payments Section */}
            <div className="preference-section">
              <h2 className="section-title">Billing & Payments</h2>
              <p className="section-description">Invoice and payment notifications</p>

              <div className="preference-group">
                <PreferenceToggle
                  label="Invoice Sent"
                  description="Receive invoices when they're ready"
                  checked={preferences.invoice_sent}
                  onChange={() => togglePreference('invoice_sent')}
                />
                <PreferenceToggle
                  label="Payment Received"
                  description="Confirmation when your payment is processed"
                  checked={preferences.payment_received}
                  onChange={() => togglePreference('payment_received')}
                />
                <PreferenceToggle
                  label="Payment Reminders"
                  description="Friendly reminders for upcoming or overdue payments"
                  checked={preferences.payment_reminder}
                  onChange={() => togglePreference('payment_reminder')}
                />
              </div>
            </div>

            {/* Farm Digest Section */}
            <div className="preference-section">
              <h2 className="section-title">Farm Digest</h2>
              <p className="section-description">Daily morning briefing for your farm</p>

              <div className="preference-group">
                <PreferenceToggle
                  label="Daily Farm Digest"
                  description="Morning email with tasks, egg production, weather, and alerts"
                  checked={preferences.daily_digest}
                  onChange={() => togglePreference('daily_digest')}
                />
              </div>
            </div>

            {/* Laura Voice Section */}
            <div className="preference-section">
              <h2 className="section-title">Laura Voice</h2>
              <p className="section-description">Voice assistant settings for Farmhand</p>

              <div className="preference-group">
                <PreferenceToggle
                  label="Laura Voice Responses"
                  description="Let Laura read back your notes and ask follow-up questions out loud"
                  checked={preferences.laura_voice_enabled}
                  onChange={() => togglePreference('laura_voice_enabled')}
                />
              </div>
            </div>

            {/* Marketing Section */}
            <div className="preference-section">
              <h2 className="section-title">Marketing & News</h2>
              <p className="section-description">Optional promotional content</p>

              <div className="preference-group">
                <PreferenceToggle
                  label="Marketing Emails"
                  description="Receive news, promotions, and product updates"
                  checked={preferences.marketing_emails}
                  onChange={() => togglePreference('marketing_emails')}
                />
              </div>
            </div>

            <div className="preferences-actions">
              <button
                className="portal-btn portal-btn-primary"
                onClick={savePreferences}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </div>
        )}
      </div>
      <PortalFooter />

      <style>{`
        .preferences-container {
          max-width: 700px;
        }

        .preference-section {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }

        .section-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #2D5A27;
          margin: 0 0 0.25rem 0;
        }

        .section-description {
          color: #666;
          font-size: 0.875rem;
          margin: 0 0 1rem 0;
        }

        .preference-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .preference-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          background: #FAFAFA;
          border-radius: 8px;
          transition: background 0.2s;
        }

        .preference-item:hover {
          background: #F5F5F5;
        }

        .preference-info {
          flex: 1;
        }

        .preference-label {
          font-weight: 500;
          color: #333;
          margin-bottom: 0.125rem;
        }

        .preference-description {
          font-size: 0.8rem;
          color: #888;
        }

        .preference-toggle {
          position: relative;
          width: 48px;
          height: 26px;
          margin-left: 1rem;
        }

        .preference-toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: 0.3s;
          border-radius: 26px;
        }

        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 20px;
          width: 20px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.3s;
          border-radius: 50%;
        }

        input:checked + .toggle-slider {
          background-color: #2D5A27;
        }

        input:checked + .toggle-slider:before {
          transform: translateX(22px);
        }

        .preferences-actions {
          margin-top: 1.5rem;
          display: flex;
          justify-content: flex-end;
        }

        .portal-success {
          background-color: #E8F5E9;
          color: #2E7D32;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1rem;
          border: 1px solid #A5D6A7;
        }

        @media (max-width: 600px) {
          .preference-section {
            padding: 1rem;
          }

          .preference-item {
            padding: 0.625rem 0.75rem;
          }

          .preference-toggle {
            width: 44px;
            height: 24px;
          }

          .toggle-slider:before {
            height: 18px;
            width: 18px;
          }

          input:checked + .toggle-slider:before {
            transform: translateX(20px);
          }
        }
      `}</style>
    </div>
  );
};

interface PreferenceToggleProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: () => void;
}

const PreferenceToggle: React.FC<PreferenceToggleProps> = ({ label, description, checked, onChange }) => (
  <div className="preference-item">
    <div className="preference-info">
      <div className="preference-label">{label}</div>
      <div className="preference-description">{description}</div>
    </div>
    <label className="preference-toggle">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="toggle-slider"></span>
    </label>
  </div>
);

export default EmailPreferences;
